import {
    ServiceItemRequiredFieldKey,
    serviceItemRequiredFieldKeys,
    serviceItemRequiredFieldLabels,
    ServiceItemRequiredFieldsGroup,
    WorkOrderRequiredFieldKey,
    workOrderRequiredFieldKeys,
    workOrderRequiredFieldLabels,
    WorkOrderRequiredFieldsGroup,
} from "@model/company/CompanySettings"
import { ServiceItemForm, serviceItemHasCostValue } from "@model/serviceRequests/ServiceItemForm"
import { Site } from "@model/site/Site"
import { CompletableTaskValue, InspectionTaskValue, Task } from "@model/Task"

/**
 * The single home for the "is this required field actually filled in" rules.
 *
 * Both the Work Order completion checks and the Service Item completion check run through here so
 * the two can never disagree, and so the popups can mark exactly the same items the buttons block.
 */

/** The subset of the Work Order form these rules read. Structural so the rules stay testable. */
export type WorkOrderRequiredFieldsSource = {
    assignedTo: number
    site: Site | null
}

/** Missing fields on the Work Order itself, in the order the admin page lists them. */
export function missingWorkOrderRequiredFields(
    form: WorkOrderRequiredFieldsSource | null,
    required: WorkOrderRequiredFieldsGroup
): WorkOrderRequiredFieldKey[] {
    if (form === null) return []

    return workOrderRequiredFieldKeys.filter((field) => {
        if (!required[field]) return false

        switch (field) {
            // 0 is the unassigned sentinel on the Work Order form, not a contact id.
            case "assignedTo":
                return form.assignedTo === 0
            case "site":
                return form.site === null
            default:
                return false
        }
    })
}

/** Missing fields on a single Service Item, in the order the admin page lists them. */
export function missingServiceItemRequiredFields(
    item: ServiceItemForm,
    required: ServiceItemRequiredFieldsGroup
): ServiceItemRequiredFieldKey[] {
    return serviceItemRequiredFieldKeys.filter((field) => {
        if (!required[field]) return false

        switch (field) {
            case "workPerformedBy":
                return item.workPerformedBy.length === 0
            case "serviceCode":
                return item.serviceCode === null
            // Hours and minutes are two inputs for one value: 0h 30m is a complete entry.
            case "laborHours":
                return item.laborHours === null && item.laborMinutes === null
            case "laborCost":
                return !serviceItemHasCostValue(item.laborCost)
            case "partsCost":
                return !serviceItemHasCostValue(item.partsCost)
            // Misc Cost is stored as otherCost.
            case "miscCost":
                return !serviceItemHasCostValue(item.otherCost)
            case "mechanicsNotes":
                return item.notes.trim() === ""
            case "measurements":
                return hasUnmeasuredTask(item.tasks)
            case "attachments":
                return item.attachments.length === 0
            default:
                return false
        }
    })
}

/**
 * Missing fields per Service Item, keyed by uiElementId. Items that are complete are left out, so
 * an empty result means the whole list passes.
 */
export function serviceItemsWithMissingRequiredFields(
    items: ServiceItemForm[],
    required: ServiceItemRequiredFieldsGroup
): Record<number, ServiceItemRequiredFieldKey[]> {
    const result: Record<number, ServiceItemRequiredFieldKey[]> = {}

    items.forEach((item) => {
        const missing = missingServiceItemRequiredFields(item, required)
        if (missing.length > 0) result[item.uiElementId] = missing
    })

    return result
}

export function labelsForWorkOrderRequiredFields(fields: WorkOrderRequiredFieldKey[]): string[] {
    return fields.map((field) => workOrderRequiredFieldLabels[field])
}

export function labelsForServiceItemRequiredFields(fields: ServiceItemRequiredFieldKey[]): string[] {
    return fields.map((field) => serviceItemRequiredFieldLabels[field])
}

/**
 * Every Service Task renders a measurement input, so "Measurements" means all of them are filled.
 * Tasks marked N/A or Not Performed are skipped: there was no work done to measure.
 */
function hasUnmeasuredTask(tasks: Task[] | null): boolean {
    if (tasks === null) return false

    return tasks.some((task) => taskNeedsMeasurement(task) && (task.measurement ?? "").trim() === "")
}

function taskNeedsMeasurement(task: Task): boolean {
    return (
        task.value !== CompletableTaskValue.NA &&
        task.value !== CompletableTaskValue.NotPerformed &&
        task.value !== InspectionTaskValue.NA &&
        task.value !== InspectionTaskValue.NotPerformed
    )
}
